import { Component, createRef } from 'react';
import isEqual from 'lodash-es/isEqual';
import { connect } from 'react-redux';
import autoBind from 'react-autobind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRedo } from '@fortawesome/pro-solid-svg-icons/faRedo';
import { faFilter } from '@fortawesome/pro-solid-svg-icons/faFilter';

import withComponentName from '../../../decorators/withComponentName';
import {
  getPresentationsData, getNewPresentationsData, resetPeriodsFilter,
} from '../../../actions/investorRelations.actions';
import { commonAPIDataLimit } from '../../../data/webPageData';
import { presentationsParamGenerate } from '../../../helpers/identifierParamHelpers';

import FilterButton from '../../Buttons/FilterButton';
import FilterBar from './FilterBar';
import Preloader from '../../Preloader';
import StoryCardLists from '../CommonComponent/StoryCardLists';
import TypesSection from '../CommonComponent/TypesSection';

import Styles from './styles.module.scss';

class InvestorRelations extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isFilterOpen: false,
      isInitialPresentationsCall: false,
      isShowMoreActive: false,
      currentPage: 1,
      hasNextPage: true,
      identifierProps: '',
      isReset: false,
    };

    autoBind(this);
    this.mounted = false;
    this.newPresentationsFetching = false;
    this.irRef = createRef();
  }

  componentDidMount() {
    this.mounted = true;
    this.setState({
      identifierProps: this.setExtraProps(),
    }, () => {
      const { permissionsInitialized } = this.props;
      if (permissionsInitialized) {
        this.loadInvestorData();
      }
    });
    const { isMainPage } = this.props;
    if (isMainPage) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { investorRelations, permissionsInitialized } = this.props;
    const { investorRelations: prevInvestorRelations } = prevProps;

    const propsParam = {
      isAllTypesSelected: investorRelations.isAllTypesSelected,
      selectedTypes: investorRelations.selectedTypes,
      isAllYearsSelected: investorRelations.isAllYearsSelected,
      fromYear: investorRelations.fromYear,
      toYear: investorRelations.toYear,
      isAllPeriodsSelected: investorRelations.isAllPeriodsSelected,
      selectedPeriods: investorRelations.selectedPeriods,
    };
    const prevPropsParam = {
      isAllTypesSelected: prevInvestorRelations.isAllTypesSelected,
      selectedTypes: prevInvestorRelations.selectedTypes,
      isAllYearsSelected: prevInvestorRelations.isAllYearsSelected,
      fromYear: prevInvestorRelations.fromYear,
      toYear: prevInvestorRelations.toYear,
      isAllPeriodsSelected: prevInvestorRelations.isAllPeriodsSelected,
      selectedPeriods: prevInvestorRelations.selectedPeriods,
    };

    if (
      !isEqual(propsParam, prevPropsParam)
      || (permissionsInitialized && permissionsInitialized !== prevProps.permissionsInitialized)
    ) {
      this.loadInvestorData();
    }

    const { isReset } = this.state;
    if (isReset && prevState.isReset !== isReset) {
      this.setState({
        isReset: false,
      });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    const { isMainPage } = this.props;
    if (isMainPage) {
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  async onScroll() {
    const docTop = this.irRef.current.offsetTop;
    const docHeight = this.irRef.current.offsetHeight;
    const winheight = window.innerHeight === undefined
      ? document.documentElement.clientHeight
      : window.innerHeight;
    const scrollpoint = window.scrollY === undefined
      ? window.document.documentElement.scrollTop
      : window.scrollY;

    if ((scrollpoint + winheight) >= (docHeight + docTop)) {
      this.loadNewPresentationsData();
    }
  }

  setExtraProps() {
    const { slug } = this.props;
    if (!slug) return '';

    const identifiers = presentationsParamGenerate(slug);
    return identifiers;
  }

  async loadNewPresentationsData() {
    const { hasNextPage, currentPage, identifierProps } = this.state;
    const {
      getNewPresentationsData: propsGetNewPresentations,
      loading, presentationsPageLoad, isMainPage,
    } = this.props;

    if (this.newPresentationsFetching || !hasNextPage || loading || presentationsPageLoad) return;

    const params = {
      per: commonAPIDataLimit,
      page: currentPage + 1,
    };
    if (!isMainPage) {
      params.identifiers = identifierProps;
    }

    this.newPresentationsFetching = true;
    const data = await propsGetNewPresentations(params);
    this.newPresentationsFetching = false;

    if (!this.mounted) return;
    if (data.length < commonAPIDataLimit) {
      this.setState({
        hasNextPage: false,
      });
    } else {
      this.setState({
        hasNextPage: true,
        currentPage: currentPage + 1,
      });
    }
  }

  loadInvestorData(isRefresh) {
    const { isMainPage, getPresentationsData: propsGetData } = this.props;
    const { identifierProps } = this.state;
    const params = {
      per: commonAPIDataLimit,
      isRefresh,
    };
    if (!isMainPage) {
      params.identifiers = identifierProps;
    }
    propsGetData(params, { isInitialCall: true })
      .then((data) => {
        this.setState({
          hasNextPage: data.length >= commonAPIDataLimit,
        });
      })
      .catch(() => {

      });
    this.setState({
      isInitialPresentationsCall: true,
      currentPage: 1,
    });
  }

  handleFilterRefreshClick() {
    this.loadInvestorData(true);
  }

  handleFilterButtonClick() {
    const { isFilterOpen } = this.state;
    this.setState({
      isFilterOpen: !isFilterOpen,
    });
  }

  handleFilterButtonClose() {
    this.setState({
      isFilterOpen: false,
    });
  }

  handleShowMoreClick() {
    const { hasNextPage, isShowMoreActive } = this.state;
    if (isShowMoreActive) {
      this.loadNewPresentationsData();
    }
    if (isShowMoreActive && !hasNextPage) {
      return this.setState({
        isShowMoreActive: false,
      });
    }
    this.setState({
      isShowMoreActive: true,
    });
  }

  handleReset() {
    const { resetPeriodsFilter: propsResetAll, piwikEnabled } = this.props;

    propsResetAll();
    this.setState({
      isReset: true,
    });

    if (piwikEnabled) {
      _paq.push(['trackEvent', 'IR filters', 'Reset filters']);
    }
  }

  render() {
    const {
      isFilterOpen, isInitialPresentationsCall, isShowMoreActive,
      hasNextPage, identifierProps, isReset,
    } = this.state;
    const {
      loading, presentations,
      presentationsPageLoad,
      newPresentationsLoading,
      isMainPage, piwikEnabled,
    } = this.props;

    const presentationsData = !isShowMoreActive && !isMainPage
      ? presentations.slice(0, 5) : presentations;
    const showCaughtUp = presentations.length <= 5
      ? hasNextPage : hasNextPage || (!isShowMoreActive && !isMainPage);

    return (
      <div className={Styles.investor_relations_container} ref={this.irRef}>
        <div className={Styles.filter_buttons}>
          <FilterButton
            icon={<FontAwesomeIcon icon={faRedo} />}
            content="Refresh"
            onClick={this.handleFilterRefreshClick}
          />
          <FilterButton
            icon={<FontAwesomeIcon icon={faFilter} />}
            content="Filters"
            active={isFilterOpen}
            onClick={this.handleFilterButtonClick}
          />
        </div>
        <FilterBar
          isFilterOpen={isFilterOpen}
          isReset={isReset}
          onReset={this.handleReset}
          handleFilterClose={this.handleFilterButtonClose}
          piwikEnabled={piwikEnabled}
        />
        {
          loading || (presentationsPageLoad && !presentations.length)
            ? (
              <div className={Styles.investor_loader_holder}>
                <Preloader loading />
              </div>
            )
            : (
              <>
                <StoryCardLists
                  cardURL="/directory/investor-relations"
                  presentations={presentationsData}
                  presentationsPageLoad={presentationsPageLoad}
                  isInitialPresentationsCall={isInitialPresentationsCall}
                  newPresentationsLoading={newPresentationsLoading}
                  hasNextPage={showCaughtUp}
                  ref={this.presentationsListsRef}
                  showCompanyTag={isMainPage}
                />
                {presentations.length && presentations.length > 5 && !isMainPage ? (
                  <div className={Styles.show_more_btn} onClick={this.handleShowMoreClick}>
                    {!isShowMoreActive || hasNextPage ? 'Show More' : 'Show Less'}
                  </div>
                ) : null}
              </>
            )
        }
        {identifierProps && !isMainPage && (
          <TypesSection
            identifierProps={identifierProps}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = ({
  investorRelations, promobar, common, subscriptions,
}) => ({
  investorRelations,
  loading: investorRelations.loading,
  presentations: investorRelations.presentations,
  presentationsPageLoad: investorRelations.presentationsPageLoad,
  newPresentationsLoading: investorRelations.newPresentationsLoading,
  statePromo: promobar,
  scrollUpPressed: common.scrollUpPressed,
  permissionsInitialized: subscriptions.permissionsInitialized,
});

const mapDispatchToProps = {
  getPresentationsData,
  getNewPresentationsData,
  resetPeriodsFilter,
};

export default withComponentName(connect(mapStateToProps, mapDispatchToProps)(InvestorRelations));
